import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";

import { EarlyAccessNotice, ProductShell } from "@/components/app/product-shell";
import { ErrorBoundary } from "@/components/error-boundary";
import { listProperties, type SavedProperty } from "@/lib/property/property.functions";

export const Route = createFileRoute("/_authenticated/app/intelligence")({
  head: () => ({
    meta: [
      { title: "Property intelligence — AROS FLOW" },
      {
        name: "description",
        content:
          "Saved property records with their data source and last update date. Distress signals and opportunity scoring are in development.",
      },
      { property: "og:title", content: "Property intelligence — AROS FLOW" },
      {
        property: "og:description",
        content: "Saved property records with sources and update dates, ready for future signals.",
      },
      { property: "og:type", content: "website" },
    ],
  }),
  component: IntelligencePage,
});

function IntelligencePage() {
  return (
    <ProductShell
      title="Intelligence"
      subtitle="Your saved property records, with the source and date behind each one."
    >
      <ErrorBoundary label="Intelligence crashed">
        <IntelligenceBody />
      </ErrorBoundary>
    </ProductShell>
  );
}

function IntelligenceBody() {
  const fetchProperties = useServerFn(listProperties);
  const { data: properties = [], isLoading } = useQuery({
    queryKey: ["properties"],
    queryFn: () => fetchProperties(),
  });

  return (
    <div className="space-y-5">
      <EarlyAccessNotice
        title="Distress signals and opportunity scoring"
        body="No distress data feed is connected. Rather than show a made-up score, these fields stay empty until a real licensed source is wired in — the records below already carry the space for them."
        requires="A licensed distress / public-records data source."
      />

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading saved properties…</p>
      ) : properties.length === 0 ? (
        <p className="rounded-xl border border-[var(--glass-border)] bg-card p-6 text-sm text-muted-foreground">
          No saved property records yet. Enriched analyses you save will appear here with their
          source and update date.
        </p>
      ) : (
        <ul className="space-y-2">
          {properties.map((p: SavedProperty) => (
            <li
              key={p.id}
              className="flex flex-wrap items-center gap-3 rounded-xl border border-[var(--glass-border)] bg-card p-4 text-xs"
            >
              <span className="font-semibold">{p.addressText}</span>
              <span className="rounded-full border border-[var(--glass-border)] px-2 py-0.5 text-[10px] uppercase tracking-wider">
                Source: {p.source}
              </span>
              <span className="text-muted-foreground">
                Updated{" "}
                {p.sourceUpdatedAt
                  ? new Date(p.sourceUpdatedAt).toLocaleDateString()
                  : "not recorded"}
              </span>
              <span className="ml-auto text-muted-foreground">
                Opportunity score: {p.opportunityScore ?? "not available"}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
